"use client";

import React, { useState } from "react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import {
  ArrowUpRight,
  Banknote,
  CalendarClock,
  Check,
  ChevronDown,
  FileText,
  MapPin,
  Package,
  Plus,
  Router,
  Sparkles,
  Truck,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { Link } from "@/i18n/routing";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import FAQItem from "@/components/common/FAQItem";
import type { InternetPlanDetailData } from "@/lib/fetch/internet";
import { INTERNET_CONNECTION_ICONS, INTERNET_EXTRA_ICONS } from "./internet-meta";
import {
  formatInternetPrice,
  formatInternetSpeed,
  getPlanCost,
  getRunningMonthlyCents,
  isMonthlyPlan,
  isSymmetricPlan,
} from "./internet-plan-math";
import InternetSpeedMeter from "./InternetSpeedMeter";

type Props = {
  data: InternetPlanDetailData;
  compare?: {
    selected: boolean;
    disabled: boolean;
    onToggle: (id: string) => void;
  };
};

type Fact = { key: string; label: string; value: string };

const DEFAULT_STAY_MONTHS = 12;

/** The full record for one plan, rendered inside the plan drawer. */
const InternetPlanDetail: React.FC<Props> = ({ data, compare }) => {
  const t = useTranslations("internet");
  const locale = useLocale();
  const format = useFormatter();
  const [stay, setStay] = useState(DEFAULT_STAY_MONTHS);

  const Icon = INTERNET_CONNECTION_ICONS[data.connectionType];
  const running = getRunningMonthlyCents(data);
  const cost = getPlanCost(data, stay);
  const price = (cents: number) => formatInternetPrice(cents, locale);
  const feeValue = (cents: number | null) =>
    cents === null
      ? t("detail.not-stated")
      : cents === 0
        ? t("detail.free")
        : price(cents);

  const facts: Fact[] = [
    {
      key: "term",
      label: t("detail.facts.term"),
      value: isMonthlyPlan(data)
        ? t("plan.term.monthly")
        : data.minimumContractMonths
          ? t("plan.term.months", { months: data.minimumContractMonths })
          : t("detail.not-stated"),
    },
    {
      key: "regular",
      label: t("detail.facts.regular-price"),
      value:
        data.regularMonthlyPriceCents !== null
          ? `${price(data.regularMonthlyPriceCents)} ${t("plan.per-month")}`
          : t("plan.price-on-check"),
    },
    data.introMonthlyPriceCents !== null &&
      data.introDurationMonths && {
        key: "intro",
        label: t("detail.facts.intro"),
        value: t("plan.intro", {
          price: price(data.introMonthlyPriceCents),
          months: data.introDurationMonths,
        }),
      },
    {
      key: "activation",
      label: t("detail.facts.activation"),
      value: feeValue(data.activationFeeCents),
    },
    data.installationFeeCents !== null && {
      key: "installation-fee",
      label: t("detail.facts.installation-fee"),
      value: feeValue(data.installationFeeCents),
    },
    data.equipmentUpfrontFeeCents !== null && {
      key: "equipment-upfront",
      label: t("detail.facts.equipment-upfront"),
      value: feeValue(data.equipmentUpfrontFeeCents),
    },
    data.equipmentMonthlyFeeCents !== null && {
      key: "equipment-monthly",
      label: t("detail.facts.equipment-monthly"),
      value:
        data.equipmentMonthlyFeeCents === 0
          ? t("detail.free")
          : `${price(data.equipmentMonthlyFeeCents)} ${t("plan.per-month")}`,
    },
    data.networkMonthlyFeeCents !== null && {
      key: "network-fee",
      label: t("detail.facts.network-fee"),
      value: data.networkFeeVaries
        ? t("detail.network-fee-from", {
            price: price(data.networkMonthlyFeeCents),
          })
        : `${price(data.networkMonthlyFeeCents)} ${t("plan.per-month")}`,
    },
    data.installationMethod !== "UNKNOWN" && {
      key: "install",
      label: t("detail.facts.installation"),
      value: t(`installation.${data.installationMethod}`),
    },
    data.equipmentOwnership && {
      key: "ownership",
      label: t("detail.facts.router"),
      value: [
        t(`ownership.${data.equipmentOwnership}`),
        data.wifiStandard,
      ]
        .filter(Boolean)
        .join(", "),
    },
    data.dataPolicy !== "UNKNOWN" && {
      key: "data",
      label: t("detail.facts.data"),
      value: t(`data-policy.${data.dataPolicy}`),
    },
  ].filter(Boolean) as Fact[];

  const technical = [
    { key: "ownRouterSupported", value: data.ownRouterSupported },
    { key: "staticIp", value: data.staticIp },
    { key: "publicIpv4", value: data.publicIpv4 },
    { key: "ipv6", value: data.ipv6 },
  ].filter((item) => item.value === true) as {
    key: "ownRouterSupported" | "staticIp" | "publicIpv4" | "ipv6";
    value: true;
  }[];

  const sections: {
    key:
      | "pricing"
      | "contract"
      | "installation"
      | "equipment"
      | "availability"
      | "moving"
      | "extras";
    icon: LucideIcon;
    text: string | null;
  }[] = [
    { key: "pricing", icon: Banknote, text: data.text.pricingDetails },
    { key: "contract", icon: CalendarClock, text: data.text.contractDetails },
    { key: "installation", icon: Wrench, text: data.text.installationDetails },
    { key: "equipment", icon: Router, text: data.text.equipmentDetails },
    { key: "availability", icon: MapPin, text: data.text.availabilityDetails },
    { key: "moving", icon: Truck, text: data.text.movingDetails },
    {
      key: "extras",
      icon: Package,
      text: data.text.additionalServicesDetails,
    },
  ];
  const visibleSections = sections.filter((section) => section.text?.trim());

  const considerations = Array.from(
    new Set([
      ...data.text.considerations,
      ...Object.values(data.text.factNotes ?? {}),
    ]),
  ).filter((note) => note?.trim());

  const checkedAt = data.lastVerifiedAt
    ? format.dateTime(new Date(data.lastVerifiedAt), {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="flex flex-col">
      <header className="flex flex-col gap-4 border-b border-border bg-slate-50 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1 text-xs font-semibold shadow-sm">
            <Icon className="size-3.5" aria-hidden />
            {t(`connection-type.${data.connectionType}`)}
          </span>
          {isSymmetricPlan(data) && (
            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold">
              {t("plan.symmetric")}
            </span>
          )}
          {isMonthlyPlan(data) && (
            <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-white">
              {t("plan.term.monthly")}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Link
            href={`/resource/internet/${data.provider.slug}`}
            title={t("plan.provider-link-title", {
              provider: data.provider.name,
            })}
            className="w-fit text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-blue-700"
          >
            {data.provider.name}
          </Link>
          <h2 className="text-2xl font-semibold tracking-tight">{data.name}</h2>
          {data.network && (
            <p className="text-sm text-muted-foreground">
              {t("plan.via-network")}{" "}
              <Link
                href={`/resource/internet/network/${data.network.slug}`}
                title={t("plan.network-link-title", {
                  network: data.network.name,
                })}
                className="font-medium text-foreground hover:text-blue-700 hover:underline"
              >
                {data.network.name}
              </Link>
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <InternetSpeedMeter
            download={data.downloadMbps}
            upload={data.uploadMbps}
            downloadLabel={t("plan.download")}
            uploadLabel={t("plan.upload")}
            downloadValue={
              data.downloadMbps !== null
                ? formatInternetSpeed(data.downloadMbps, locale)
                : ""
            }
            uploadValue={
              data.uploadMbps !== null
                ? formatInternetSpeed(data.uploadMbps, locale)
                : ""
            }
            unknownLabel={t("plan.not-stated")}
            className="rounded-xl bg-background p-3 shadow-sm"
          />
          <div className="flex flex-col sm:items-end">
            {running !== null ? (
              <p className="flex items-baseline gap-1">
                <span className="text-3xl font-semibold tracking-tight tabular-nums">
                  {price(running)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t("plan.per-month")}
                </span>
              </p>
            ) : (
              <p className="text-base font-semibold">
                {t("plan.price-on-check")}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="secondary">
            <a
              href={data.postcodeCheckUrl}
              target="_blank"
              rel="noreferrer nofollow"
              title={t("plan.check-address-title", {
                provider: data.provider.name,
              })}
            >
              <MapPin className="size-4" />
              {t("plan.check-address")}
            </a>
          </Button>
          <Button asChild variant="outline">
            <a
              href={data.officialUrl}
              target="_blank"
              rel="noreferrer nofollow"
              title={t("detail.official-title", {
                plan: data.name,
                provider: data.provider.name,
              })}
            >
              {t("detail.official")}
              <ArrowUpRight className="size-4" />
            </a>
          </Button>
          {compare && (
            <Button
              type="button"
              variant="outline"
              aria-pressed={compare.selected}
              disabled={compare.disabled && !compare.selected}
              onClick={() => compare.onToggle(data.id)}
            >
              {compare.selected ? (
                <Check className="size-4" />
              ) : (
                <Plus className="size-4" />
              )}
              {compare.selected ? t("plan.compare-added") : t("plan.compare")}
            </Button>
          )}
        </div>
      </header>

      <div className="flex flex-col gap-8 p-6">
        {data.text.description && (
          <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">
            {data.text.description}
          </p>
        )}

        {cost && (
          <section className="flex flex-col gap-4 rounded-2xl bg-secondary p-5">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold sm:text-base">
                {t("detail.stay.title")}
              </h3>
              <p className="text-sm text-secondary-foreground/80">
                {t("detail.stay.description")}
              </p>
            </div>
            <div className="flex flex-col gap-3 rounded-xl bg-background p-4 shadow-sm">
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-muted-foreground">
                  {t("detail.stay.label")}
                </span>
                <span className="font-semibold tabular-nums">
                  {t("common.months", { count: stay })}
                </span>
              </div>
              <Slider
                min={1}
                max={36}
                step={1}
                value={[stay]}
                onValueChange={([value]) => setStay(value)}
                aria-label={t("detail.stay.label")}
                className="[&_[role=slider]]:size-6"
              />
              <dl className="grid grid-cols-2 gap-3 pt-1">
                <div className="flex flex-col gap-0.5">
                  <dt className="text-xs text-muted-foreground">
                    {t("detail.stay.total")}
                  </dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {price(cost.totalCents)}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5">
                  <dt className="text-xs text-muted-foreground">
                    {t("detail.stay.effective")}
                  </dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {price(cost.effectiveMonthlyCents)}
                  </dd>
                </div>
              </dl>
              {cost.termOutlastsStay && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
                  {t("detail.stay.term-warning", {
                    months: cost.billedMonths,
                  })}
                </p>
              )}
              {!cost.activationKnown && (
                <p className="text-xs leading-5 text-muted-foreground">
                  {t("detail.stay.fees-unknown")}
                </p>
              )}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold sm:text-base">
            {t("detail.facts.title")}
          </h3>
          <dl className="flex flex-col divide-y divide-border rounded-2xl border border-border px-4">
            {facts.map((fact) => (
              <div
                key={fact.key}
                className="flex items-start justify-between gap-4 py-3"
              >
                <dt className="text-sm text-muted-foreground">{fact.label}</dt>
                <dd className="min-w-0 text-right text-sm font-semibold">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>
          {technical.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {technical.map((item) => (
                <li
                  key={item.key}
                  className="inline-flex min-h-7 items-center gap-1 rounded-full bg-muted px-2.5 text-xs font-semibold"
                >
                  <Check className="size-3.5 text-emerald-600" aria-hidden />
                  {t(`detail.technical.${item.key}`)}
                </li>
              ))}
            </ul>
          )}
        </section>

        {data.additionalServices.length > 0 && (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold sm:text-base">
              {t("detail.extras.title")}
            </h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {data.additionalServices.map((service) => {
                const ExtraIcon = INTERNET_EXTRA_ICONS[service.type];
                const amount =
                  service.monthlyPriceCents !== null &&
                  service.monthlyPriceCents !== undefined
                    ? service.monthlyPriceCents === 0
                      ? t("detail.free")
                      : `${price(service.monthlyPriceCents)} ${t("plan.per-month")}`
                    : service.included
                      ? t("detail.extras.included")
                      : null;
                return (
                  <li
                    key={service.key}
                    className="flex items-center gap-3 rounded-xl border border-border p-3"
                  >
                    <span className="flex size-9 flex-none items-center justify-center rounded-lg bg-secondary">
                      <ExtraIcon className="size-4" aria-hidden />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm font-semibold">
                        {t(`extra.${service.type}`)}
                      </span>
                      {amount && (
                        <span className="text-xs text-muted-foreground">
                          {amount}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {visibleSections.length > 0 && (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold sm:text-base">
              {t("detail.sections.title")}
            </h3>
            <div className="flex flex-col gap-2">
              {visibleSections.map((section, index) => {
                const SectionIcon = section.icon;
                return (
                  <details
                    key={section.key}
                    open={index === 0}
                    className="group rounded-2xl border border-border bg-background open:border-zinc-300"
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-3 p-4 text-sm font-semibold">
                      <span className="flex size-8 flex-none items-center justify-center rounded-lg bg-muted">
                        <SectionIcon className="size-4" aria-hidden />
                      </span>
                      <span className="flex-1">
                        {t(`detail.sections.${section.key}`)}
                      </span>
                      <ChevronDown className="size-4 flex-none text-muted-foreground transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="whitespace-pre-line px-4 pb-4 text-sm leading-7 text-muted-foreground">
                      {section.text}
                    </p>
                  </details>
                );
              })}
            </div>
          </section>
        )}

        {considerations.length > 0 && (
          <section className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-950 sm:text-base">
              <Sparkles className="size-4" aria-hidden />
              {t("detail.considerations")}
            </h3>
            <ul className="flex flex-col gap-2">
              {considerations.map((note) => (
                <li
                  key={note}
                  className="flex items-start gap-2 text-sm leading-6 text-amber-950"
                >
                  <span
                    className="mt-2 size-1.5 flex-none rounded-full bg-amber-500"
                    aria-hidden
                  />
                  {note}
                </li>
              ))}
            </ul>
          </section>
        )}

        {data.text.faqs.length > 0 && (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold sm:text-base">
              {t("detail.faq", { plan: data.name })}
            </h3>
            <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background">
              {data.text.faqs.map((faq, index) => (
                <FAQItem
                  key={`${faq.q}-${index}`}
                  question={faq.q}
                  answer={<span className="whitespace-pre-line">{faq.a}</span>}
                  collapsible
                />
              ))}
            </div>
          </section>
        )}

        {data.sources.length > 0 && (
          <section className="flex flex-col gap-3 border-t border-border pt-6">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="size-4" aria-hidden />
              {t("detail.sources.title")}
            </h3>
            {checkedAt && (
              <p className="text-xs leading-5 text-muted-foreground">
                {t("detail.sources.checked", { date: checkedAt })}
              </p>
            )}
            <ul className="flex flex-col gap-1.5">
              {data.sources.map((source, index) => {
                let host = source.url;
                try {
                  host = new URL(source.url).hostname.replace(/^www\./, "");
                } catch {
                  // keep the raw url
                }
                return (
                  <li key={`${source.id}-${index}`}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer nofollow"
                      title={t("detail.sources.link-title", {
                        provider: data.provider.name,
                      })}
                      className="inline-flex max-w-full items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
                    >
                      <span className="truncate">
                        {t("detail.sources.item", {
                          index: index + 1,
                          host,
                        })}
                      </span>
                      <ArrowUpRight className="size-3 flex-none" aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default InternetPlanDetail;
